import { configureStore, ThunkAction, Action, Middleware, MiddlewareAPI, isRejectedWithValue } from "@reduxjs/toolkit";
import { backendApi } from "./apislices/backendApi";
import { healthApi } from "./apislices/healthApi";
import { pqIndexApi } from "./apislices/pqIndexApi";
import { requestApi } from "./apislices/requestApi";
import { routerApi } from "./apislices/routerApi";
import { trendApi } from "./apislices/trendApi";
import lambdaControlSlice from "../routes/lambdaControl/lambdaControlSlice";
import customAutocompleteSlice from "../features/customAutocomplete/customAutocompleteSlice";
import globalSnackbarSlice, { openSnackbar } from "../features/globalSnackbar/globalSnackbarSlice";
import adminPageSlice from "../features/adminPage/adminPageSlice";
import pqEventsSlice from "../routes/pqEvents/pqEventsSlice";
import dataPlausibilitySlice from "../routes/dataPlausibility/dataPlausibilitySlice";
import { activeMeasuringPointApi } from "./apislices/activeMeasuringPointApi";
import { connectedSiteApi } from "./apislices/connectedSiteApi";
import { operationalEventApi } from "./apislices/operationalEventApi";
import { dataFieldApi } from "./apislices/dataFieldApi";
import { measuringPointAttachmentApi } from "./apislices/measuringPointAttachmentApi";
import { thresholdApi } from "./apislices/thresholdApi";
import { measuringPointGroupApi } from "./apislices/measuringPointGroupApi";
import { configurationApi } from "./apislices/configurationApi";
import { deviceApi } from "./apislices/deviceApi";
import { deviceModelApi } from "./apislices/deviceModelApi";
import { measuringPointApi } from "./apislices/measuringPointApi";
import { measurementApi } from "./apislices/measurementApi";
import { measurementExportApi } from "./apislices/measurementExportApi";
import { routerModelApi } from "./apislices/routerModelApi";
import { switchgearApi } from "./apislices/switchgearApi";
import { voltageLevelApi } from "./apislices/voltageLevelApi";
import { voltageTransformerApi } from "./apislices/voltageTransformerApi";
import { incidentApi } from "./apislices/incidentApi";
import { currentTransformerApi } from "./apislices/currentTransformerApi";
import { substationApi } from "./apislices/substationApi";
import { maintenanceCycleApi } from "./apislices/maintenanceCycleApi";
import { timeSynchronisationTypeApi } from "./apislices/timeSynchronisationTypeApi";
import { markedMeasurementLogApi } from "./apislices/markedMeasurementLogApi";
import { gridEventApi } from "./apislices/gridEventApi";
import { correlationApi } from "./apislices/correlationApi";
import { anomalyApi } from "./apislices/anomalyApi";
import masterDataSlice from "../routes/administration/masterData/masterDataSlice";
import groupAdministrationSlice from "../routes/pqEvents/groupAdministration/groupAdministrationSlice";
import pqIndexSlice from "../routes/analyse/pqIndex/pqIndexSlice";
import operationalEventsSlice from "../routes/operationalEvents/operationalEventsSlice";
import dataCompletenessSlice from "../routes/dataCompleteness/dataCompletenessSlice";
import anomalySlice from "../routes/analyse/anomaly/anomalySlice";
import firewallRequestSlice from "../routes/firewallRequest/firewallRequestSlice";
import { setupListeners } from "@reduxjs/toolkit/query";
import correlationSlice from "../routes/analyse/correlation/correlationSlice";
import trendsSlice from "../routes/analyse/trends/trendsSlice";
import dashboardAnalysisSlice from "../routes/dashboardAnalysis/dashboardAnalysisSlice";
import runRequestManagementSlice from "../routes/runRequestManagement/runRequestManagementSlice";
import activeMeasuringPointsSlice from "../routes/activeMeasuringPoints/activeMeasuringPointsSlice";
import { transformerModelApi } from "./apislices/transformerModelApi";

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    if (action.meta.arg.endpointName !== "getHealthGeneral") {
      console.error(action);
      api.dispatch(
        openSnackbar({
          message: "Es ist ein Fehler aufgetreten.",
          severity: "error",
          dateAndTime: action.meta.baseQueryMeta.response?.headers.get("date") ?? new Date().toISOString(),
          statusCode: action.payload.status,
          requestUrl: action.meta.baseQueryMeta.request.url,
          xRequestID: action.meta.baseQueryMeta.response?.headers.get("x-request-id"),
        })
      );
    } //TODO do not show snackbar when health endpoint is also failing
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    [activeMeasuringPointApi.reducerPath]: activeMeasuringPointApi.reducer,
    [connectedSiteApi.reducerPath]: connectedSiteApi.reducer,
    [anomalyApi.reducerPath]: anomalyApi.reducer,
    [backendApi.reducerPath]: backendApi.reducer,
    [operationalEventApi.reducerPath]: operationalEventApi.reducer,
    [dataFieldApi.reducerPath]: dataFieldApi.reducer,
    [measuringPointAttachmentApi.reducerPath]: measuringPointAttachmentApi.reducer,
    [markedMeasurementLogApi.reducerPath]: markedMeasurementLogApi.reducer,
    [thresholdApi.reducerPath]: thresholdApi.reducer,
    [measuringPointGroupApi.reducerPath]: measuringPointGroupApi.reducer,
    [healthApi.reducerPath]: healthApi.reducer,
    [configurationApi.reducerPath]: configurationApi.reducer,
    [correlationApi.reducerPath]: correlationApi.reducer,
    [deviceApi.reducerPath]: deviceApi.reducer,
    [deviceModelApi.reducerPath]: deviceModelApi.reducer,
    [measuringPointApi.reducerPath]: measuringPointApi.reducer,
    [measurementApi.reducerPath]: measurementApi.reducer,
    [measurementExportApi.reducerPath]: measurementExportApi.reducer,
    [gridEventApi.reducerPath]: gridEventApi.reducer,
    [pqIndexApi.reducerPath]: pqIndexApi.reducer,
    [requestApi.reducerPath]: requestApi.reducer,
    [routerApi.reducerPath]: routerApi.reducer,
    [routerModelApi.reducerPath]: routerModelApi.reducer,
    [switchgearApi.reducerPath]: switchgearApi.reducer,
    [voltageLevelApi.reducerPath]: voltageLevelApi.reducer,
    [voltageTransformerApi.reducerPath]: voltageTransformerApi.reducer,
    [incidentApi.reducerPath]: incidentApi.reducer,
    [currentTransformerApi.reducerPath]: currentTransformerApi.reducer,
    [trendApi.reducerPath]: trendApi.reducer,
    [substationApi.reducerPath]: substationApi.reducer,
    [maintenanceCycleApi.reducerPath]: maintenanceCycleApi.reducer,
    [timeSynchronisationTypeApi.reducerPath]: timeSynchronisationTypeApi.reducer,
    [transformerModelApi.reducerPath]: transformerModelApi.reducer,
    globalSnackbar: globalSnackbarSlice,
    customAutocomplete: customAutocompleteSlice,
    adminPage: adminPageSlice,
    lambdaControl: lambdaControlSlice,
    pqEvents: pqEventsSlice,
    operationalEvents: operationalEventsSlice,
    dataPlausibility: dataPlausibilitySlice,
    masterData: masterDataSlice,
    groupAdministration: groupAdministrationSlice,
    pqIndex: pqIndexSlice,
    dataCompleteness: dataCompletenessSlice,
    anomaly: anomalySlice,
    firewallRequest: firewallRequestSlice,
    correlation: correlationSlice,
    trends: trendsSlice,
    dashboardAnalysis: dashboardAnalysisSlice,
    runRequestManagement: runRequestManagementSlice,
    activeMeasuringPoints: activeMeasuringPointsSlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(rtkQueryErrorLogger)
      .concat(activeMeasuringPointApi.middleware)
      .concat(connectedSiteApi.middleware)
      .concat(anomalyApi.middleware)
      .concat(backendApi.middleware)
      .concat(operationalEventApi.middleware)
      .concat(dataFieldApi.middleware)
      .concat(measuringPointAttachmentApi.middleware)
      .concat(markedMeasurementLogApi.middleware)
      .concat(thresholdApi.middleware)
      .concat(measuringPointGroupApi.middleware)
      .concat(healthApi.middleware)
      .concat(configurationApi.middleware)
      .concat(correlationApi.middleware)
      .concat(deviceApi.middleware)
      .concat(deviceModelApi.middleware)
      .concat(measuringPointApi.middleware)
      .concat(measurementApi.middleware)
      .concat(measurementExportApi.middleware)
      .concat(gridEventApi.middleware)
      .concat(pqIndexApi.middleware)
      .concat(requestApi.middleware)
      .concat(routerApi.middleware)
      .concat(routerModelApi.middleware)
      .concat(switchgearApi.middleware)
      .concat(voltageLevelApi.middleware)
      .concat(voltageTransformerApi.middleware)
      .concat(incidentApi.middleware)
      .concat(currentTransformerApi.middleware)
      .concat(trendApi.middleware)
      .concat(substationApi.middleware)
      .concat(maintenanceCycleApi.middleware)
      .concat(timeSynchronisationTypeApi.middleware)
      .concat(transformerModelApi.middleware),
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
